import { useRef, useEffect, useState } from 'react';
import { Volume2, VolumeX, Play } from 'lucide-react';

export default function VideoPlayer({ src, className = '', autoPlay = true }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayBtn, setShowPlayBtn] = useState(false);

  useEffect(() => {
    if (!autoPlay || !containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const video = videoRef.current;
        if (!video) return;
        if (entry.isIntersecting) {
          video.play().catch(() => {});
          setIsPlaying(true);
          setShowPlayBtn(false);
        } else {
          video.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [autoPlay, src]);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      setIsPlaying(true);
      setShowPlayBtn(false);
    } else {
      video.pause();
      setIsPlaying(false);
      setShowPlayBtn(true);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} onClick={togglePlay} data-testid="video-player">
      <video
        ref={videoRef}
        src={src}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        className="w-full max-h-[70vh] object-contain bg-black"
        onPause={() => { setIsPlaying(false); setShowPlayBtn(true); }}
        onPlay={() => { setIsPlaying(true); setShowPlayBtn(false); }}
      />

      {showPlayBtn && !isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm">
            <Play className="w-8 h-8 text-white fill-white ml-1" />
          </div>
        </div>
      )}

      <button
        onClick={toggleMute}
        className="absolute bottom-3 right-3 p-2 bg-black/60 rounded-full text-white hover:bg-black/80 transition-colors z-10"
        data-testid="video-mute-toggle"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
    </div>
  );
}
